import { type ComponentType, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApprovalDetail } from '@app/shared';
import {
  Banner,
  Button,
  Cluster,
  CodeBlock,
  ConfirmDialog,
  type DescriptionItem,
  DescriptionList,
  ErrorState,
  Icon,
  Icons,
  Link,
  type LinkProps,
  LoadingState,
  Panel,
  PaneHeader,
  StatusBadge,
  Stack,
  Surface,
  Text,
  TextArea,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useWorkspaceId } from '../lib/auth';
import { isSendClassTool } from '../lib/sendClass';
import { toolLabel } from '../lib/toolLabels';
import { RunStatusBadge } from '../components/RunStatusBadge';

/**
 * The ui `Link` rendered as a TanStack router `Link` (`as={RouterLink}`). The primitive's
 * prop type is closed over anchor attributes, so we widen it here to carry the router's
 * `to`/`params` — chakra forwards them to the underlying RouterLink at runtime.
 */
const RouterRefLink = Link as unknown as ComponentType<
  LinkProps & { to: string; params: Record<string, string> }
>;

/**
 * Approval detail (§10, §14) — the DETAIL PANE of the Approvals split view (Phase 3b)
 * and the human control point. The trust-critical review surface: a human "what will
 * happen" render, the affected recipients, the EXACT arguments (JSON) with the args
 * hash, and the pinned content snapshot for a by-reference send. Approve / reject wakes
 * the run; the platform (not the model) executes an approved call with exactly these
 * arguments. A send-class tool (email.send) is irreversible, so it reads "Approve &
 * send" and takes one explicit confirm. The controls mirror the in-place approval on
 * the Run page so the two review surfaces feel identical.
 *
 * The parent ApprovalsInboxPage owns the SSE subscription (this pane always renders
 * inside it), so we don't open a second EventSource here.
 */

/** Normalize an args value that may be a single address or a list into `string[]`. */
function toStringArray(v: unknown): string[] | undefined {
  if (typeof v === 'string') return [v];
  if (Array.isArray(v)) {
    const strings = v.filter((x): x is string => typeof x === 'string');
    return strings.length > 0 ? strings : undefined;
  }
  return undefined;
}

/** The `affected` blob as a plain record (it's an opaque jsonObject on the wire). */
function affectedRecord(approval: ApprovalDetail): Record<string, unknown> | undefined {
  return approval.affected && typeof approval.affected === 'object'
    ? (approval.affected as Record<string, unknown>)
    : undefined;
}

/** All resolved recipient addresses (pinned snapshot > affected blob > raw args). */
function resolveRecipients(approval: ApprovalDetail): string[] {
  const snap = approval.contentSnapshot?.recipients;
  const args = approval.arguments as Record<string, unknown>;
  const affected = affectedRecord(approval);
  const fromAffected = toStringArray(affected?.recipients);
  const fromArgs = toStringArray(args.to) ?? toStringArray(args.recipients);
  return snap ?? fromAffected ?? fromArgs ?? [];
}

/**
 * A human descriptor for WHO receives a send when no explicit address list is
 * materialized (e.g. a newsletter dispatched to its configured audience) — so the
 * recipients block is never blank for a send.
 */
function resolveAudience(approval: ApprovalDetail): string | undefined {
  const affected = affectedRecord(approval);
  if (typeof affected?.audience === 'string') return affected.audience;
  const count = affected?.recipientCount ?? affected?.audienceSize ?? affected?.count;
  if (typeof count === 'number' && count > 0) {
    return `${count} ${count === 1 ? 'recipient' : 'recipients'}`;
  }
  return isSendClassTool(approval.tool) ? 'its configured audience' : undefined;
}

/** `sendAt` → "Send at"; `newsletterId` → "Newsletter id". Presentation only. */
function prettifyKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._]+/g, ' ')
    .trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Args already surfaced elsewhere (recipients / email body) or too large to inline. */
const SUMMARY_HIDDEN_ARGS = new Set(['to', 'recipients', 'cc', 'bcc', 'body', 'html', 'subject']);

/** A compact scalar-argument summary for the generic (non-email) "what will happen". */
function keyArgItems(args: Record<string, unknown>): DescriptionItem[] {
  const items: DescriptionItem[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (SUMMARY_HIDDEN_ARGS.has(key.toLowerCase())) continue;
    let text: string | undefined;
    if (typeof value === 'string') text = value;
    else if (typeof value === 'number' || typeof value === 'boolean') text = String(value);
    if (!text) continue;
    items.push({
      term: prettifyKey(key),
      description: text.length > 140 ? `${text.slice(0, 140)}…` : text,
    });
    if (items.length >= 6) break;
  }
  return items;
}

/**
 * The canonical, prominent recipients treatment — the single loudest fact on a send:
 * WHO it reaches. Rendered once (previously duplicated across a "To" row and a separate
 * "Affected recipients" list). Carries the `affected-recipients` testid. Shows each
 * materialized address as a high-contrast pill (with a count when many), or names the
 * audience descriptor when the exact list isn't pinned.
 */
function Recipients({ recipients, audience }: { recipients: string[]; audience?: string }) {
  if (recipients.length === 0 && !audience) return null;
  const many = recipients.length > 4;
  return (
    <Surface tone="subtle" bordered padding="4" radius="lg" data-testid="affected-recipients">
      <Stack gap="2">
        <Cluster gap="2" align="center">
          <Icon as={many ? Icons.Users : Icons.User} size="sm" tone="action" />
          <Text size="sm" weight="semibold">
            {recipients.length === 1 ? 'Recipient' : 'Recipients'}
          </Text>
          {many && (
            <Text size="xs" tone="muted">
              {recipients.length} people
            </Text>
          )}
        </Cluster>
        {recipients.length > 0 ? (
          <Cluster gap="2" wrap>
            {recipients.map((r) => (
              <StatusBadge key={r} tone="info" size="md" label={r} />
            ))}
          </Cluster>
        ) : (
          <Text size="md" weight="medium">
            {audience}
          </Text>
        )}
      </Stack>
    </Surface>
  );
}

/** Email-shaped render (subject + pinned body hash + body) for email.send / draft args. */
function EmailSummary({
  subject,
  body,
  bodyHash,
}: {
  subject?: string;
  body?: string;
  bodyHash?: string;
}) {
  const items: DescriptionItem[] = [];
  if (subject) items.push({ term: 'Subject', description: subject });
  if (bodyHash)
    items.push({ term: 'Body hash', description: <CodeBlock inline>{bodyHash}</CodeBlock> });
  return (
    <Stack gap="3">
      {items.length > 0 && <DescriptionList items={items} />}
      {body && (
        <Stack gap="1">
          <Text size="xs" weight="medium" tone="muted">
            Body
          </Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {body}
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

/** Generic fallback for a non-email send-class action (e.g. dispatch a newsletter). */
function GenericSummary({ tool, items }: { tool: string; items: DescriptionItem[] }) {
  return (
    <Stack gap="3">
      <Stack gap="1">
        <Text size="xs" weight="medium" tone="muted">
          Action
        </Text>
        <Text size="sm" weight="semibold">
          {toolLabel(tool)}
        </Text>
      </Stack>
      {items.length > 0 && <DescriptionList items={items} />}
    </Stack>
  );
}

/**
 * "What will happen" — the human-legible summary of the proposed action. Leads with the
 * canonical recipients block (the loudest fact for a send), then an email-shaped body
 * when the args look like a message, or a generic tool-label + key-argument summary
 * otherwise. NEVER empty for a send: an irreversible action must never force the operator
 * to trust the raw JSON alone at the moment of approval.
 */
function WhatWillHappen({ approval }: { approval: ApprovalDetail }) {
  const snap = approval.contentSnapshot;
  const args = approval.arguments as Record<string, unknown>;
  const recipients = resolveRecipients(approval);
  const audience = resolveAudience(approval);
  const subject = snap?.subject ?? (typeof args.subject === 'string' ? args.subject : undefined);
  const body = typeof args.body === 'string' ? args.body : undefined;
  const isEmailShaped = Boolean(subject || body || snap?.bodyHash);

  return (
    <Stack gap="4" data-testid="human-render">
      <Recipients recipients={recipients} audience={audience} />
      {isEmailShaped ? (
        <EmailSummary subject={subject} body={body} bodyHash={snap?.bodyHash} />
      ) : (
        <GenericSummary tool={approval.tool} items={keyArgItems(args)} />
      )}
    </Stack>
  );
}

export function ApprovalDetailPage() {
  const workspaceId = useWorkspaceId();
  const { approvalId } = useParams({ from: '/app/approvals/$approvalId' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  // Send-class approvals (email.send etc.) are irreversible — approving fires the send.
  // They go through a lightweight confirm; non-send approvals stay single-click.
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);

  const approval = useQuery({
    queryKey: qk.approval(workspaceId, approvalId),
    queryFn: () => api.getApproval(workspaceId, approvalId),
  });

  // #300: the approval's run may have been FLAGGED because a newer message arrived on its
  // thread while it was parked for approval — warn the operator the draft may be outdated.
  const runId = approval.data?.runId;
  const run = useQuery({
    queryKey: qk.run(workspaceId, runId ?? ''),
    queryFn: () => api.getRun(workspaceId, runId as string),
    enabled: !!runId,
  });

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      api.decideApproval(workspaceId, approvalId, decision, note || undefined),
    onSuccess: async (res, decision) => {
      const executed = res.status === 'approved';
      setBanner({
        kind: decision === 'approved' && !executed ? 'error' : 'success',
        text:
          decision === 'rejected'
            ? 'Rejected. The run was woken with your note.'
            : executed
              ? 'Approved. The platform executed the action with exactly these arguments.'
              : `Approved, but the platform could not execute it (status: ${res.status}).`,
      });
      await queryClient.invalidateQueries({ queryKey: qk.approval(workspaceId, approvalId) });
      await queryClient.invalidateQueries({ queryKey: qk.approvals(workspaceId) });
    },
    onError: (err) =>
      setBanner({ kind: 'error', text: err instanceof ApiError ? err.message : 'Decision failed.' }),
  });

  if (approval.isLoading)
    return <LoadingState variant="skeleton" rows={6} label="Loading approval…" />;
  if (approval.isError) {
    return (
      <ErrorState
        description={
          approval.error instanceof ApiError ? approval.error.message : 'Could not load this approval.'
        }
        onRetry={() => void approval.refetch()}
      />
    );
  }

  const a = approval.data as ApprovalDetail;
  const decided = a.status !== 'pending';
  const sendClass = isSendClassTool(a.tool);

  return (
    <Stack gap="5">
      {/* Header — the action, its status, and its risk read at a glance. */}
      <PaneHeader
        title="Review approval"
        status={<RunStatusBadge status={a.status} />}
        meta={
          <Cluster gap="2" wrap>
            <CodeBlock inline>{a.tool}</CodeBlock>
            <RunStatusBadge status={a.risk} size="sm" />
            {/* Risk badge carries the alarm; "will send" is a calm neutral qualifier
                (de-dup the double-red — see ApprovalsInboxPage). */}
            {sendClass && <StatusBadge tone="neutral" size="sm" icon={Icons.Send} label="will send" />}
          </Cluster>
        }
      />

      {banner && (
        <Banner
          tone={banner.kind === 'error' ? 'danger' : 'success'}
          onDismiss={() => setBanner(null)}
          data-testid="approval-banner"
        >
          {banner.text}
        </Banner>
      )}

      {/* #300: newer-message warning — the run was flagged because a newer message arrived on
          its thread. Reuse the "runs on this thread" link so the operator can review it. */}
      {run.data?.run.newerThreadMessage && (
        <Banner
          tone="warning"
          icon={Icons.AlertTriangle}
          data-testid="approval-newer-thread-banner"
          action={
            run.data.run.threadId ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  void navigate({
                    to: '/app/runs',
                    search: (prev) => ({
                      ...prev,
                      threadId: run.data?.run.threadId ?? undefined,
                      outcome: 'all',
                    }),
                  })
                }
                data-testid="approval-newer-thread-link"
              >
                View all runs on this thread
              </Button>
            ) : undefined
          }
        >
          A newer message has arrived on this thread — this draft may be outdated.
        </Banner>
      )}

      {/* What will happen — the human-legible summary of the proposed action. */}
      <Panel title="What will happen">
        <WhatWillHappen approval={a} />
      </Panel>

      {/* Exact arguments — the platform executes with EXACTLY this payload (§10). */}
      <Panel
        title="Exact arguments"
        description={
          <>
            The platform executes with exactly these arguments (args hash{' '}
            <CodeBlock inline>{a.argsHash.slice(0, 12)}</CodeBlock>).
          </>
        }
      >
        <CodeBlock value={a.arguments} />
      </Panel>

      {a.contentSnapshot && (
        <Panel
          title="Pinned content snapshot"
          description="Approving pins this content; if the draft changes before execution the send is refused and re-approval is required (§10)."
        >
          <CodeBlock value={a.contentSnapshot} />
        </Panel>
      )}

      {/* Agent's reason + the run this approval belongs to. */}
      <Panel title="Agent's reason">
        <Stack gap="3">
          <Text size="sm" tone="secondary">
            {a.reason || 'No reason provided.'}
          </Text>
          <Text size="sm" tone="muted">
            Run{' '}
            <RouterRefLink as={RouterLink} to="/app/runs/$runId/full" params={{ runId: a.runId }}>
              {a.runId.slice(0, 8)}
            </RouterRefLink>
          </Text>
        </Stack>
      </Panel>

      {/* Decision — Approve is the confident primary; Reject stays quiet. */}
      <Panel title="Decision">
        {decided ? (
          <Text size="sm" tone="secondary">
            This approval is {a.status}.
          </Text>
        ) : (
          <Stack gap="3">
            <TextArea
              aria-label="Note (optional)"
              placeholder="Note (optional)"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Cluster gap="3" wrap>
              <Button
                variant="primary"
                onClick={() => (sendClass ? setSendConfirmOpen(true) : decide.mutate('approved'))}
                loading={decide.isPending}
                data-testid="approve"
              >
                {sendClass ? 'Approve & send' : 'Approve'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => decide.mutate('rejected')}
                disabled={decide.isPending}
                data-testid="reject"
              >
                Reject
              </Button>
            </Cluster>
          </Stack>
        )}
      </Panel>

      {/* Send-class confirm (email.send etc.). Approving executes the send with exactly
          the pinned arguments — irreversible — so it takes one explicit confirm. Only
          this Confirm calls decide.mutate('approved'). */}
      <ConfirmDialog
        open={sendConfirmOpen}
        onClose={() => setSendConfirmOpen(false)}
        onConfirm={() => {
          setSendConfirmOpen(false);
          decide.mutate('approved');
        }}
        title="Send this now?"
        tone="primary"
        confirmLabel="Approve & send"
        cancelLabel="Cancel"
        loading={decide.isPending}
        confirmTestId="confirm-approve-send"
        cancelTestId="send-cancel"
        data-testid="send-confirm-dialog"
        description={
          <Text tone="secondary" size="md">
            Approving <CodeBlock inline>{a.tool}</CodeBlock> <strong>sends immediately</strong> with
            exactly the arguments shown. This is a real, irreversible action taken on your behalf —
            it cannot be unsent.
          </Text>
        }
      />
    </Stack>
  );
}
